import { createPage, pageHeight, renderParentChain, moveBreakAfterAvoids, isOverflowing } from './page'
import {
  getBreakStyle,
  lowestChildNode,
  nodeMargin,
  parentBottomSpacing,
  removeBrokenElement,
  loadImages,
  hasContent,
  removeEmptyParents,
  selectByDataRef,
  deepClone,
  containsPrintedTag,
  hasTextInTree,
  hasText,
} from './dom'
import { addMovedTextToParent, removeUnusedNodes, reversedWords, splitOnWord } from './text_splitter'
import last from 'lodash/last'

export default async function renderFlexingItem(flexElement, parentChain, pages) {
  let page = pages[pages.length - 1]
  const renderedParent = renderParentChain(parentChain, page)
  const newFlexItem = renderedParent.appendChild(deepClone(flexElement))
  await loadImages(newFlexItem)

  let movedAlready = false
  if (isTooLowOnPage(newFlexItem, page)) {
    const newPage = createPage()
    pages.push(newPage)
    moveFullItem(newFlexItem, newPage, page, parentChain)
    page = newPage
    movedAlready = true
  }

  const overflowingNodes = overflowingNodesFromPage(newFlexItem, page)

  if (overflowingNodes.length > 0) {
    breakFlexItem(newFlexItem, parentChain, overflowingNodes, pages)
  } else if (isOverflowing(page) && !movedAlready) {
    const newPage = createPage()
    pages.push(newPage)
    moveFullItem(newFlexItem, newPage, page, parentChain)
  }
}

function isTooLowOnPage(flexElement, page) {
  const styles = window.getComputedStyle(flexElement)
  const minHeight = Number.parseInt(styles.getPropertyValue('min-height'))
  return (
    offsetTopToPage(flexElement, page) + minHeight + nodeMargin(flexElement) + parentBottomSpacing(flexElement) >
    pageHeight(page)
  )
}

function breakFlexItem(newFlexItem, parentChain, overflowingNodes, pages) {
  const page = pages[pages.length - 1]
  const newPage = createPage()
  pages.push(newPage)

  if (getBreakStyle(newFlexItem)['inside'] === 'avoid') {
    moveFullItem(newFlexItem, newPage, page, parentChain)
  } else {
    breakUpItemInChunks(page, newPage, overflowingNodes, parentChain, newFlexItem, pages)
  }
}

function moveFullItem(newFlexItem, newPage, originalPage, parentChain) {
  removeBrokenElement(newFlexItem)
  const renderedParent = renderParentChain(parentChain, newPage)
  renderedParent.appendChild(newFlexItem)
  moveBreakAfterAvoids(originalPage, newPage)
}

function breakUpItemInChunks(lastPage, currentPage, overflowingNodes, parentChain, newFlexItem, pages) {
  let tries = 0
  let lastFlexItem = newFlexItem
  while (overflowingNodes.length > 0 && tries < 10) {
    const renderedParent = renderParentChain(parentChain, currentPage)
    const nextPageFlex = renderedParent.appendChild(cloneFlexItem(newFlexItem))
    moveOverflow(nextPageFlex, newFlexItem, lastPage, overflowingNodes)
    overflowingNodes = overflowingNodesFromPage(nextPageFlex, currentPage)
    if (overflowingNodes.length > 0) {
      lastPage = currentPage
      currentPage = createPage()
      pages.push(currentPage)
    }
    adjustBorders(lastFlexItem, nextPageFlex)
    lastFlexItem = nextPageFlex
    tries++
  }
  if (newFlexItem.childNodes.length === 0) {
    removeBrokenElement(newFlexItem)
  }
}

function adjustBorders(lastPageFlex, newPageFlex) {
  const emptyRefs = []
  newPageFlex.querySelectorAll(':scope > .border-solid').forEach((child) => {
    if (Array.from(child.childNodes).length === 0) {
      child.classList.add('border-0')
      emptyRefs.push(child.getAttribute('data-ref'))
    } else {
      child.classList.remove('border-b-0')
      child.style.removeProperty('border-bottom-right-radius')
      child.style.removeProperty('border-bottom-left-radius')

      child.classList.add('border-t-0')
      child.style.setProperty('border-top-right-radius', '0px', 'important')
      child.style.setProperty('border-top-left-radius', '0px', 'important')
    }
  })
  lastPageFlex.querySelectorAll(':scope > .border-solid').forEach((child) => {
    if (Array.from(child.childNodes).length === 0) {
      child.classList.add('border-0')
    } else if (!emptyRefs.includes(child.getAttribute('data-ref'))) {
      child.classList.add('border-b-0')
      child.style.setProperty('border-bottom-right-radius', '0px', 'important')
      child.style.setProperty('border-bottom-left-radius', '0px', 'important')
    }
  })
}

function cloneFlexItem(flexItem) {
  const copiedNode = flexItem.cloneNode()
  Array.from(flexItem.children).forEach((child) => {
    copiedNode.appendChild(child.cloneNode())
  })
  return copiedNode
}

function overflowingNodesFromPage(flexItem, page) {
  const overflowingNodes = []
  Array.from(flexItem.childNodes)
    .flatMap((child) => Array.from(child.childNodes))
    .forEach((node) => {
      if (nodeOffPage(node, page)) {
        addOverflowingNode(overflowingNodes, node, page)
      }
    })
  return overflowingNodes
}

function addOverflowingNode(overflowingNodes, node, page) {
  const baseElements = getBaseElements(node)
  if (baseElements.length > 0) {
    Array.from(baseElements).forEach((element) => {
      if (nodeOffPage(element, page)) {
        overflowingNodes.push(element)
      }
    })
  } else {
    overflowingNodes.push(node)
  }
}

function getBaseElements(node) {
  if (hasContent(node) || node.children.length === 0 || getBreakStyle(node).inside === 'avoid') {
    return [node]
  } else {
    return Array.from(node.children)
      .map((node) => getBaseElements(node))
      .flat()
  }
}

function moveOverflow(newFlexParent, flexParent, page, overflowingNodes) {
  let tries = 0
  // break nodes until they fit in the flexParent
  while (overflowingNodes.length > 0 && tries < 200) {
    overflowingNodes.forEach((node) => {
      iterateNodeBreak(node, newFlexParent, flexParent, pageHeight(page))
    })
    overflowingNodes = overflowingNodes.filter((node) => {
      return page.contains(node) && nodeOffPage(node, page)
    })
    tries++
  }
  fixLists(newFlexParent, flexParent)
  return []
}

function fixLists(newFlexParent, oldFlexParent) {
  newFlexParent.querySelectorAll('ol,ul').forEach((list) => {
    list.querySelectorAll('li').forEach((li) => {
      if (!containsPrintedTag(li) && !hasText(li) && !hasTextInTree(li)) {
        li.remove()
      }
    })
    const oldList = selectByDataRef(list, oldFlexParent)
    if (oldList) {
      if (oldList.children.length > 0) {
        setNewStart(list, oldList)
        const li = list.querySelector(':scope > li')
        if (li) {
          li.style = 'list-style: none;'
        }
      } else {
        oldList.remove()
      }
    }
  })
}

function setNewStart(list, oldList) {
  const newStart = oldList.querySelectorAll('li').length + parseInt(oldList.getAttribute('start') || 0)
  list.setAttribute('start', newStart)
}

function nodeOffPage(node, page) {
  return (
    offsetTopToPage(node, page) + node.offsetHeight + nodeMargin(node) + parentBottomSpacing(node) > pageHeight(page)
  )
}

function offsetTopToPage(node, page) {
  const pageTop = page.querySelector('.page-content')
  let top = node.offsetTop
  let offsetParent = node.offsetParent
  while (offsetParent && offsetParent !== pageTop) {
    top += offsetParent.offsetTop
    offsetParent = offsetParent.offsetParent
  }
  return top
}

function iterateNodeBreak(node, newFlexParent, flexParent, pageHeight) {
  if (node.nodeName === '#comment') {
    node.remove()
  } else if (node.nodeName === '#text') {
    const { movedText, wordStart } = splitOnWord(node, reversedWords(node)[0])
    addMovedTextToParent(newFlexParent, flexParent, movedText, node)
    removeUnusedNodes(node, wordStart)
  } else if (node.nodeName === 'TABLE') {
    breakTable(node, newFlexParent)
  } else if (multiBreakableNode(node) && lowestChildNode(node)) {
    iterateNodeBreak(lowestChildNode(node), newFlexParent, flexParent, pageHeight)
  } else if (cannotBreakNode(node)) {
    const oldParent = node.parentElement
    addOverflowToNewChunk(node, newFlexParent)
    removeEmptyParents(oldParent)
  } else {
    iterateNodeBreak(lowestChildNode(node), newFlexParent, flexParent, pageHeight)
  }
}

function cannotBreakNode(node) {
  return (
    node.querySelector &&
    (node.querySelector('.math-tex') ||
      node.childNodes.length === 0 ||
      (getBreakStyle(node).inside === 'avoid' && node.clientHeight < pageHeight) ||
      hasContent(node))
  )
}

function multiBreakableNode(node) {
  return ['B', 'LI', 'A', 'I'].includes(node.nodeName)
}

function breakTable(table, newFlexParent) {
  const lastRow = last(table.querySelectorAll('tr:last-child'))
  if (!lastRow) {
    return
  }

  if (!selectByDataRef(table, newFlexParent)) {
    const continuation = table.cloneNode()
    // A continuation loses its `<thead>` (it stays on the first page) and only
    // gets a repeated copy re-added after chunking, in
    // normalizeTableHeadersAcrossPages. Reserve the header's height here, while
    // the pager is still deciding how many rows fit, so the repeated header
    // doesn't inflate an already-full page and clip the last row. The reserved
    // space (not a real `<thead>`) is swapped for the actual header by that
    // later pass.
    reserveRepeatedHeaderSpace(table, continuation)
    appendContinuationInSplitOrder(buildOrSelectNewParent(table, newFlexParent), continuation)
  }

  const newParent = buildOrSelectNewParent(lastRow, newFlexParent)
  const newTable = selectByDataRef(table, newFlexParent)
  maintainTableWidth(table, newTable)

  newParent.prepend(lastRow)

  if (!table.querySelector('tr')) {
    table.remove()
  }
}

// A wide author table split by column groups (split_wide_tables) becomes several
// stacked sibling tables carrying a `data-split-order`. The pager breaks overflow
// bottom-up, so a lower group's continuation is created before a higher group's;
// a plain append would then place the lower group first and reverse the columns'
// reading order across pages. Insert the continuation before the first already-
// placed sibling with a greater split order so the groups stay in order. Tables
// without a split order (ordinary tables) keep the previous append behaviour.
export function appendContinuationInSplitOrder(newParent, continuation) {
  const order = Number.parseInt(continuation.dataset.splitOrder)
  if (Number.isNaN(order)) {
    newParent.append(continuation)
    return
  }
  const laterSibling = Array.from(newParent.children).find((child) => {
    const childOrder = Number.parseInt(child.dataset ? child.dataset.splitOrder : NaN)
    return !Number.isNaN(childOrder) && childOrder > order
  })
  if (laterSibling) {
    newParent.insertBefore(continuation, laterSibling)
  } else {
    newParent.append(continuation)
  }
}

// Reserves vertical space for the header that normalizeTableHeadersAcrossPages
// will prepend to this continuation after chunking, so the pager accounts for
// the repeated header's height when deciding how many rows fit. The reservation
// is recorded on the continuation so the later pass can drop it once the real
// header is in place.
function reserveRepeatedHeaderSpace(source, continuation) {
  const height = repeatedHeaderHeight(source)
  if (height > 0) {
    continuation.style.paddingTop = `${height}px`
    continuation.dataset.repeatedHeaderReserve = height
  }
}

// The height a repeated header will occupy on a continuation. When the source
// still has its `<thead>` (the first page) it is measured directly; when the
// source is itself a continuation the reservation is carried forward.
function repeatedHeaderHeight(source) {
  const thead = source.querySelector(':scope > thead')
  if (thead) {
    return thead.offsetHeight
  }
  const carried = Number.parseFloat(source.dataset.repeatedHeaderReserve)
  return carried > 0 ? carried : 0
}

function maintainTableWidth(table, newTable) {
  const tableWidth = window.getComputedStyle(table).width
  newTable.style.width = tableWidth
  table.style.width = tableWidth
}

function addOverflowToNewChunk(node, newFlexParent) {
  const newParent = buildOrSelectNewParent(node, newFlexParent)
  newParent.append(node)
}

function buildOrSelectNewParent(node, newFlexContainer) {
  const parents = []
  let newParent = node.parentElement
  while (!selectByDataRef(newParent, newFlexContainer.parentElement)) {
    parents.unshift(newParent)
    newParent = newParent.parentElement
  }
  if (parents.length) {
    let lastParent = selectByDataRef(newParent, newFlexContainer.parentElement)
    for (const parent of parents) {
      lastParent = lastParent.appendChild(parent.cloneNode())
    }
    return lastParent
  } else {
    return selectByDataRef(newParent, newFlexContainer.parentElement)
  }
}
